import React from 'react';
import { Button } from '../components/Button';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
export function HomePage() {
  return <>
      <SEO title="Aspect — Verified Architectural Imagery Infrastructure" description="Aspect provides rights-cleared, verified photography from real buildings, governed for enterprise use." data-id="element-392" />
      <div className="space-y-24 md:space-y-32" data-id="element-393">
        {/* Hero Section - Restrained */}
        <section className="max-w-2xl" data-id="element-394">
          <h1 className="text-3xl md:text-4xl font-medium leading-tight mb-8 text-aspect-black" data-id="element-395">
            Verified, rights-cleared photography from real buildings.
          </h1>
          <div className="space-y-6 text-lg text-aspect-black/80 leading-relaxed" data-id="element-396">
            <p data-id="element-397">
              Aspect is the rights and verification infrastructure for
              real-world architectural imagery.
            </p>
            <p data-id="element-398">
              We turn completed projects into documented, licensable image
              assets by securing permissions, confirming products, and attaching
              durable metadata.
            </p>
            <p data-id="element-399">
              Aspect is not a traditional photography studio, stock library, or
              marketing service.
            </p>
            <p data-id="element-400">
              It is a system designed to make images of the built world
              trustworthy, reusable, and operational at scale.
            </p>
          </div>
          <div className="mt-10" data-id="element-401">
            <Link to="/manufacturers/request" data-id="element-402">
              <Button variant="primary" icon data-id="element-403">
                Request access
              </Button>
            </Link>
          </div>
        </section>

        {/* The Problem */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-404">
          <div className="md:col-span-4" data-id="element-405">
            <span className="text-aspect-gray" data-id="element-406">01. The Problem</span>
          </div>
          <div className="md:col-span-8 space-y-8" data-id="element-407">
            <h2 className="text-2xl font-medium" data-id="element-408">The Problem Aspect Solves</h2>
            <div className="space-y-6 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-409">
              <p data-id="element-410">
                Images of real installations exist everywhere, but most cannot
                be safely reused.
              </p>
              <ul className="list-disc pl-5 space-y-2 text-aspect-gray" data-id="element-411">
                <li data-id="element-412">Rights are unclear.</li>
                <li data-id="element-413">Products are not verified.</li>
                <li data-id="element-414">Attribution is inconsistent.</li>
                <li data-id="element-415">Usage decisions are slow and risky.</li>
              </ul>
              <p data-id="element-416">
                As a result, manufacturers rely on bespoke shoots, incomplete
                archives, or imagery that cannot withstand legal or enterprise
                scrutiny.
              </p>
              <p data-id="element-417">
                Aspect exists to replace this fragmentation with a single,
                governed system.
              </p>
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-418">
          <div className="md:col-span-4" data-id="element-419">
            <span className="text-aspect-gray" data-id="element-420">02. The System</span>
          </div>
          <div className="md:col-span-8 space-y-8" data-id="element-421">
            <h2 className="text-2xl font-medium" data-id="element-422">How the System Works</h2>
            <div className="space-y-6 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-423">
              <p data-id="element-424">Aspect operates as a standardized workflow:</p>
              <ol className="list-decimal pl-5 space-y-3 text-aspect-black/80" data-id="element-425">
                <li data-id="element-426">Completed projects are identified and approved.</li>
                <li data-id="element-427">Photography is commissioned under defined standards.</li>
                <li data-id="element-428">Products and systems shown in images are verified.</li>
                <li data-id="element-429">
                  Rights and permissions are secured at the project level.
                </li>
                <li data-id="element-430">Images enter a structured library with full context.</li>
                <li data-id="element-431">
                  Manufacturers license assets with clarity and confidence.
                </li>
              </ol>
              <p className="text-aspect-gray border-l-2 border-aspect-gray/20 pl-4 py-1" data-id="element-432">
                Each step is governed. Nothing enters the system without meeting
                all requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Trustworthiness */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-433">
          <div className="md:col-span-4" data-id="element-434">
            <span className="text-aspect-gray" data-id="element-435">03. Trust</span>
          </div>
          <div className="md:col-span-8 space-y-12" data-id="element-436">
            <h2 className="text-2xl font-medium" data-id="element-437">
              What Makes Aspect Trustworthy
            </h2>

            <div className="grid gap-8" data-id="element-438">
              <div className="space-y-2" data-id="element-439">
                <h3 className="text-lg font-medium" data-id="element-440">Rights</h3>
                <p className="text-aspect-black/70" data-id="element-441">
                  Every image is cleared before access. Usage terms are explicit
                  and enforceable.
                </p>
              </div>

              <div className="space-y-2" data-id="element-442">
                <h3 className="text-lg font-medium" data-id="element-443">Verification</h3>
                <p className="text-aspect-black/70" data-id="element-444">
                  Products are confirmed at the project level. Images are
                  evidence, not illustration.
                </p>
              </div>

              <div className="space-y-2" data-id="element-445">
                <h3 className="text-lg font-medium" data-id="element-446">Metadata</h3>
                <p className="text-aspect-black/70" data-id="element-447">
                  Project, product, and permission data are first-class. An
                  image without context is incomplete.
                </p>
                <p className="text-sm text-aspect-gray mt-2" data-id="element-448">
                  If any of these are missing, the asset does not ship.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Who it is for */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12 pb-12" data-id="element-449">
          <div className="md:col-span-4" data-id="element-450">
            <span className="text-aspect-gray" data-id="element-451">04. Stakeholders</span>
          </div>
          <div className="md:col-span-8 space-y-12" data-id="element-452">
            <h2 className="text-2xl font-medium" data-id="element-453">Who Aspect Is For</h2>

            <div className="grid md:grid-cols-2 gap-x-8 gap-y-12" data-id="element-454">
              <div className="space-y-4" data-id="element-455">
                <h3 className="font-medium text-lg" data-id="element-456">For Manufacturers</h3>
                <p className="text-aspect-black/70 text-sm min-h-[3rem]" data-id="element-457">
                  Request access to verified, rights-cleared imagery of real
                  installations.
                </p>
                <Link to="/manufacturers/request" data-id="element-458">
                  <Button variant="text" icon data-id="element-459">
                    Request access
                  </Button>
                </Link>
              </div>

              <div className="space-y-4" data-id="element-460">
                <h3 className="font-medium text-lg" data-id="element-461">For Architects</h3>
                <p className="text-aspect-black/70 text-sm min-h-[3rem]" data-id="element-462">
                  Document completed work with full attribution and control.
                </p>
                <Link to="/architects/submit" data-id="element-463">
                  <Button variant="text" icon data-id="element-464">
                    Submit a project
                  </Button>
                </Link>
              </div>

              <div className="space-y-4" data-id="element-465">
                <h3 className="font-medium text-lg" data-id="element-466">For Owners</h3>
                <p className="text-aspect-black/70 text-sm min-h-[3rem]" data-id="element-467">
                  Enable downstream usage without sacrificing governance or
                  privacy.
                </p>
                <Link to="/owners/submit" data-id="element-468">
                  <Button variant="text" icon data-id="element-469">
                    Submit a project
                  </Button>
                </Link>
              </div>

              <div className="space-y-4" data-id="element-470">
                <h3 className="font-medium text-lg" data-id="element-471">For Photographers</h3>
                <p className="text-aspect-black/70 text-sm min-h-[3rem]" data-id="element-472">
                  Participate in a professional system with clear rights and
                  predictable compensation.
                </p>
                <Link to="/photographers/apply" data-id="element-473">
                  <Button variant="text" icon data-id="element-474">
                    Work with aspect
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>;
}